import SearchableCountrySelect from "../components/SearchableCountrySelect";
import "./AdvancedSearch.css";
import { useState, useEffect } from "react";
import { get } from "../http/api";
import {
  FaSearch,
  FaLock,
  FaRunning,
  FaShieldAlt,
  FaGavel,
  FaBullhorn,
  FaUserTie,
  FaVideo
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import premiumCopy from '../premium/copy.json';
import countriesData from "../auth/register/data/countries.json";
import { getProfileDisplayName } from "../utils/profileDisplayName";


export default function AdvancedSearch({ setResults }) {

  const navigate = useNavigate();
  const location = useLocation();
  const [access, setAccess] = useState('loading');
  const [accessAttempt, setAccessAttempt] = useState(0);
  const [searchError, setSearchError] = useState(false);

  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const { t, i18n } = useTranslation();
  const copy = premiumCopy[(i18n.resolvedLanguage || i18n.language || 'fr').split('-')[0]] || premiumCopy.fr;

  useEffect(() => {
    let active = true;
    const check = async () => {
      setAccess('loading');
      setShowResults(false);
      setSearchResults([]);
      try {
        const response = await get('network/search-access');
        if (!active) return;
        setAccess(response.data?.isPremium === true ? 'allowed' : response.data?.isPremium === false ? 'locked' : 'error');
      } catch {
        if (active) setAccess('error');
      }
    };
    check();
    window.addEventListener('focus', check);
    return () => { active = false; window.removeEventListener('focus', check); };
  }, [accessAttempt]);

  const showPremium = () => {
    const params = new URLSearchParams(location.search);
    params.set('premiumFeature', 'advanced-search');
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  const getRoleLabel = (role) => {
    const normalizedRole = String(role || "").trim().toLowerCase();
    return normalizedRole
      ? t(`matchCard.${normalizedRole}`, { defaultValue: role })
      : "";
  };


  const [filters, setFilters] = useState({
    role: "",
    sport: "",
    country: "",
    position: "",
    level: "",
    speciality: "",
    type: "",
    age_min: "",
    age_max: "",
    height_min: "",
    height_max: ""
  });

  const roles = [
    {
      key: "athlete",
      label: t("advancedSearch.roles.athlete"),
      icon: <FaRunning color="var(--primary-200)" />
    },
    {
      key: "club",
      label: t("advancedSearch.roles.club"),
      icon: <FaShieldAlt color="var(--primary-200)" />
    },
    {
      key: "lawyer",
      label: t("advancedSearch.roles.lawyer"),
      icon: <FaGavel color="var(--primary-200)" />
    },
    {
      key: "brand",
      label: t("advancedSearch.roles.brand"),
      icon: <FaBullhorn color="var(--primary-200)" />
    },
    {
      key: "staff",
      label: t("advancedSearch.roles.staff"),
      icon: <FaUserTie color="var(--primary-200)" />
    },
    {
      key: "media",
      label: t("advancedSearch.roles.media"),
      icon: <FaVideo color="var(--primary-200)" />
    }
  ];

  const handleChange = (e) => {
    setFilters(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const chooseRole = (role) => {
    setFilters({
      role,
      sport: "",
      country: "",
      position: "",
      level: "",
      speciality: "",
      type: "",
      age_min: "",
      age_max: "",
      height_min: "",
      height_max: ""
    });
  };


  const handleSearch = async () => {
    if (access !== 'allowed') return;
    setSearchError(false);
    try {
      setLoading(true);
      setShowResults(true);

      const query = new URLSearchParams(filters).toString();

      const res = await get(`network/search?${query}`);

      const data = res.data || [];

      setSearchResults(data);

      if (setResults) {
        setResults(data);
      }

    } catch (err) {
      setShowResults(false);
      setSearchResults([]);
      if (setResults) setResults([]);
      if (err.message === 'PREMIUM_REQUIRED') setAccess('locked');
      else setSearchError(true);
    } finally {
      setLoading(false);
    }
  };


  if (access !== 'allowed') return (
    <section className="advanced-search" dir={i18n.dir()}>
      <h2>{t('advancedSearch.title')}</h2>
      <div className="advanced-search-promo">
        <FaLock aria-hidden="true" className="advanced-search-promo-icon" />
        <h3>{copy.searchTitle}</h3>
        <p>{copy.searchDescription}</p>
        <div className="advanced-search-preview" aria-hidden="true">
          {roles.map(role => <span key={role.key}>{role.icon} {role.label}</span>)}
        </div>
        <p className="advanced-search-filter-preview">{copy.searchFilters}</p>
        {access === 'loading' ? <p role="status">{copy.loading}</p>
          : access === 'error' ? <><p role="alert">{copy.accessError}</p><button type="button" onClick={() => setAccessAttempt(v => v + 1)}>{copy.retry}</button></>
          : <button type="button" onClick={showPremium}>{copy.searchCta}</button>}
      </div>
    </section>
  );

  return (
    <div className="advanced-search">
      <h2>{t("advancedSearch.title")}</h2>
      {searchError && <p role="alert">{copy.searchError}</p>}

      <div className="role-grid">
        {roles.map(role => (
          <button
            key={role.key}
            className={`role-card ${filters.role === role.key ? "active" : ""
              }`}
            onClick={() => chooseRole(role.key)}
          >
            <span className="role-icon">{role.icon}</span>
            <span>{role.label}</span>
          </button>
        ))}
      </div>


      {filters.role && (
        <div className="dynamic-fields">

          <SearchableCountrySelect
            name="country"
            value={filters.country}
            onChange={handleChange}
          >
            <option value="">
              {t("advancedSearch.countries.all")}
            </option>

            {countriesData.map((country) => (
              <option key={country.code} value={country.name}>
                {country.flag} {country.name}
              </option>
            ))}

          </SearchableCountrySelect>

          {filters.role === "athlete" && (
            <>
              <select
                name="sport"
                value={filters.sport}
                onChange={handleChange}
              >
                <option value="">
                  {t("advancedSearch.sports.all")}
                </option>

                <option value="Football">
                  {t("advancedSearch.sports.football")}
                </option>

                <option disabled style={{ color: "#999" }}>
                  {t("advancedSearch.sports.basketSoon")}
                </option>

                <option disabled style={{ color: "#999" }}>
                  {t("advancedSearch.sports.handballSoon")}
                </option>
              </select>

              <select
                name="position"
                value={filters.position}
                onChange={handleChange}
                disabled={!filters.sport}
              >
                {!filters.sport ? (
                  <option value="">
                    {t("advancedSearch.position.chooseSport")}
                  </option>
                ) : (
                  <>
                    <option value="">
                      {t("advancedSearch.position.all")}
                    </option>

                    {filters.sport === "Football" && (
                      <>
                        <option value="Gardien">
                          {t("advancedSearch.position.goalkeeper")}
                        </option>

                        <option value="Défenseur">
                          {t("advancedSearch.position.defender")}
                        </option>

                        <option value="Milieu">
                          {t("advancedSearch.position.midfielder")}
                        </option>

                        <option value="Attaquant">
                          {t("advancedSearch.position.forward")}
                        </option>
                      </>
                    )}
                  </>
                )}
              </select>

              <select
                name="level"
                value={filters.level}
                onChange={handleChange}
              >
                <option value="">
                  {t("advancedSearch.levels.all")}
                </option>

                <option value="Amateur">
                  {t("advancedSearch.levels.amateur")}
                </option>

                <option value="Semi-pro">
                  {t("advancedSearch.levels.semiPro")}
                </option>

                <option value="Professionnel">
                  {t("advancedSearch.levels.professional")}
                </option>

                <option value="Élite">
                  {t("advancedSearch.levels.elite")}
                </option>
              </select>

              <div className="flex-row">
                <input
                  type="number"
                  name="age_min"
                  placeholder={t("advancedSearch.age.min")}
                  value={filters.age_min}
                  onChange={handleChange}
                />

                <input
                  type="number"
                  name="age_max"
                  placeholder={t("advancedSearch.age.max")}
                  value={filters.age_max}
                  onChange={handleChange}
                />
              </div>

              <div className="flex-row">
                <input
                  type="number"
                  name="height_min"
                  placeholder={t("advancedSearch.height.min")}
                  value={filters.height_min}
                  onChange={handleChange}
                />

                <input
                  type="number"
                  name="height_max"
                  placeholder={t("advancedSearch.height.max")}
                  value={filters.height_max}
                  onChange={handleChange}
                />
              </div>
            </>
          )}


          {filters.role === "club" && (
            <>
              <select
                name="sport"
                value={filters.sport}
                onChange={handleChange}
              >
                <option value="">
                  {t("advancedSearch.sports.all")}
                </option>

                <option value="Football">
                  {t("advancedSearch.sports.football")}
                </option>

                <option disabled style={{ color: "#999" }}>
                  {t("advancedSearch.sports.basketSoon")}
                </option>

                <option disabled style={{ color: "#999" }}>
                  {t("advancedSearch.sports.handballSoon")}
                </option>
              </select>

              <select
                name="level"
                value={filters.level}
                onChange={handleChange}
              >
                <option value="">
                  {t("advancedSearch.levels.all")}
                </option>

                <option value="Amateur">
                  {t("advancedSearch.levels.amateur")}
                </option>

                <option value="Semi-pro">
                  {t("advancedSearch.levels.semiPro")}
                </option>

                <option value="Professionnel">
                  {t("advancedSearch.levels.professional")}
                </option>

                <option value="Élite">
                  {t("advancedSearch.levels.elite")}
                </option>
              </select>

            </>
          )}


          {filters.role === "lawyer" && (
            <>
              <select
                name="sport"
                value={filters.sport}
                onChange={handleChange}
              >
                <option value="">
                  {t("advancedSearch.sports.all")}
                </option>

                <option value="Football">
                  {t("advancedSearch.sports.football")}
                </option>

                <option disabled style={{ color: "#999" }}>
                  {t("advancedSearch.sports.basketSoon")}
                </option>

                <option disabled style={{ color: "#999" }}>
                  {t("advancedSearch.sports.handballSoon")}
                </option>
              </select>

            </>
          )}


          {filters.role === "brand" && (
            <>

              <select
                name="type"
                value={filters.type}
                onChange={handleChange}
              >
                <option value="">
                  {t("advancedSearch.brand.all")}
                </option>

                <option value="Vetements sportifs">
                  {t("advancedSearch.brand.sportswear")}
                </option>

                <option value="Equipementiers">
                  {t("advancedSearch.brand.equipment")}
                </option>

                <option value="Marque sportive">
                  {t("advancedSearch.brand.sportBrand")}
                </option>

                <option value="Entreprise locale / nationale">
                  {t("advancedSearch.brand.localCompany")}
                </option>

                <option value="Sponsor d evenements">
                  {t("advancedSearch.brand.eventSponsor")}
                </option>
              </select>

            </>
          )}


          {filters.role === "staff" && (
            <>
              <select
                name="sport"
                value={filters.sport}
                onChange={handleChange}
              >
                <option value="">
                  {t("advancedSearch.sports.all")}
                </option>

                <option value="Football">
                  {t("advancedSearch.sports.football")}
                </option>

                <option disabled style={{ color: "#999" }}>
                  {t("advancedSearch.sports.basketSoon")}
                </option>

                <option disabled style={{ color: "#999" }}>
                  {t("advancedSearch.sports.handballSoon")}
                </option>
              </select>

              <select
                name="speciality"
                value={filters.speciality}
                onChange={handleChange}
              >
                <option value="">
                  {t("advancedSearch.staff.all")}
                </option>

                <option value="coach">
                  {t("advancedSearch.staff.coach")}
                </option>

                <option value="physical_trainer">
                  {t("advancedSearch.staff.physicalTrainer")}
                </option>

                <option value="video_analyst">
                  {t("advancedSearch.staff.videoAnalyst")}
                </option>

                <option value="physio">
                  {t("advancedSearch.staff.physio")}
                </option>

                <option value="nutritionist">
                  {t("advancedSearch.staff.nutritionist")}
                </option>

                <option value="mental_coach">
                  {t("advancedSearch.staff.mentalCoach")}
                </option>

                <option value="referee">
                  {t("advancedSearch.staff.referee")}
                </option>
              </select>

            </>
          )}


          {filters.role === "media" && (
            <>
              <select
                name="type"
                value={filters.type}
                onChange={handleChange}
              >
                <option value="">
                  {t("advancedSearch.media.all")}
                </option>

                <option value="mediaForm.professional">
                  {t("advancedSearch.media.journalist")}
                </option>

                <option value="mediaForm.company">
                  {t("advancedSearch.media.company")}
                </option>
              </select>

            </>
          )}

          <button className="search-btn" onClick={handleSearch}>
            <FaSearch />
            {t("advancedSearch.search")}
          </button>
        </div>
      )}

      {showResults && (
        <div className="search-modal-overlay">

          <div className="search-modal">

            <div className="search-modal-header">
              <button
                className="back-btn"
                onClick={() => setShowResults(false)}
              >
                ←
              </button>

              <h3>{t("advancedSearch.results.title")}</h3>

              <span>{searchResults.length}</span>
            </div>

            <div className="search-results-list">

              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="search-user skeleton-user">
                    <div className="skeleton skeleton-avatar"></div>

                    <div className="user-lines">
                      <div className="skeleton line"></div>
                      <div className="skeleton line small"></div>
                    </div>
                  </div>
                ))
              ) : searchResults.length === 0 ? (
                <div className="empty-search">
                  <h4>{t("advancedSearch.results.empty")}</h4>

                  <p>
                    {t("advancedSearch.results.emptyDescription")}
                  </p>
                </div>
              ) : (
                searchResults.map(user => (
                  <div
                    key={user.id_user}
                    className="search-user"
                  >
                    <div className="search-avatar">
                      {getProfileDisplayName(user)?.[0] || "?"}
                    </div>

                    <div className="search-user-info">
                      <strong>{user.username}</strong>

                      <span>
                        {getProfileDisplayName(user)}
                      </span>

                      <small>
                        {getRoleLabel(user.role)} • {user.country}
                      </small>
                    </div>
                    <button
                      className="view-profile-btn-search"
                      onClick={() => navigate(`/user/${user.username}`)}
                    >
                      {t("matchCard.viewProfile")}
                    </button>
                  </div>
                ))
              )}

            </div>

          </div>

        </div>
      )}
    </div>
  );
}
